<template>
  <div class="sidebar-container" :class="{ 'sidebar-collapsed': isSidebarCollapsed }">
    <!-- Toggle Button für mobile Ansicht -->
    <button 
      class="sidebar-toggle-button mobile-only" 
      @click="toggleSidebar"
      :aria-label="isSidebarCollapsed ? 'Sidebar öffnen' : 'Sidebar schließen'"
      :aria-expanded="!isSidebarCollapsed"
    >
      <span class="toggle-icon">
        <svg v-if="isSidebarCollapsed" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="3" y1="12" x2="21" y2="12"></line>
          <line x1="3" y1="6" x2="21" y2="6"></line>
          <line x1="3" y1="18" x2="21" y2="18"></line>
        </svg>
        <svg v-else xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </span>
    </button>
    
    <!-- Toggle Button für Desktop-Ansicht -->
    <button 
      class="collapse-button desktop-only" 
      @click="toggleSidebar"
      :aria-label="isSidebarCollapsed ? 'Sidebar öffnen' : 'Sidebar schließen'"
      :aria-expanded="!isSidebarCollapsed"
    >
      <span class="collapse-icon">
        <svg v-if="isSidebarCollapsed" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="9 18 15 12 9 6"></polyline>
        </svg>
        <svg v-else xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="15 18 9 12 15 6"></polyline>
        </svg>
      </span>
    </button>
    
    <!-- Overlay für mobile Ansicht -->
    <div 
      v-if="!isSidebarCollapsed && width < 768" 
      class="sidebar-overlay" 
      @click="closeSidebarOnMobile"
    ></div>
    
    <!-- Sidebar-Inhalt -->
    <div class="sidebar" :class="{ 'sidebar-hidden': isMobile && isSidebarCollapsed, 'sidebar-open': !isSidebarCollapsed }">
      
      <!-- Tool-spezifische Navigation (dynamisch geladen für Tool-Routen) -->
      <component v-if="isAuthenticated && isToolRoute && currentToolNavigation" :is="currentToolNavigation" :collapsed="isSidebarCollapsed" />
      
      <!-- Admin-Bereich (nur für Admins UND nur auf Admin-Routen) -->
      <div v-if="isAuthenticated && isAdmin && isAdminRoute" class="admin-section">
        <div class="section-header">
          <h3>Administration</h3>
        </div>
        <div class="admin-links">
          <router-link to="/admin" class="nav-link" :class="{ active: isAdminRoute }">
            <svg class="nav-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"/>
            </svg>
            <span class="nav-text">Dashboard</span>
          </router-link>
          <router-link to="/admin/users" class="nav-link" :class="{ active: route.path === '/admin/users' }">
            <svg class="nav-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
            </svg>
            <span class="nav-text">Benutzer</span>
          </router-link>
          <router-link to="/admin/tools" class="nav-link" :class="{ active: route.path === '/admin/tools' }">
            <svg class="nav-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
              <circle cx="9" cy="9" r="2"></circle>
              <path d="M21 15.5c-.621 0-1-.379-1V12"></path>
            </svg>
            <span class="nav-text">Tools</span>
          </router-link>
          <router-link to="/admin/groups" class="nav-link" :class="{ active: route.path === '/admin/groups' }">
            <svg class="nav-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
              <rect x="9" y="9" width="12" height="12" rx="2" ry="2"></rect>
              <rect x="15" y="15" width="6" height="6" rx="1" ry="1"></rect>
            </svg>
            <span class="nav-text">Tool-Gruppen</span>
          </router-link>
          <router-link to="/admin/permissions" class="nav-link" :class="{ active: route.path === '/admin/permissions' }">
            <svg class="nav-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
              <circle cx="12" cy="16" r="1"></circle>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
            </svg>
            <span class="nav-text">Berechtigungen</span>
          </router-link>
          <router-link to="/admin/audit-logs" class="nav-link" :class="{ active: route.path === '/admin/audit-logs' }">
            <svg class="nav-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14,2 14,8 20,8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
              <polyline points="10,9 9,9 8,9"></polyline>
            </svg>
            <span class="nav-text">Audit-Logs</span>
          </router-link>
          <router-link to="/admin/credentials" class="nav-link" :class="{ active: route.path === '/admin/credentials' }">
            <svg class="nav-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"></path>
            </svg>
            <span class="nav-text">Credentials</span>
          </router-link>
          <router-link to="/admin/email" class="nav-link" :class="{ active: route.path === '/admin/email' }">
            <svg class="nav-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
              <polyline points="22,6 12,13 2,6"></polyline>
            </svg>
            <span class="nav-text">E-Mail</span>
          </router-link>
        </div>
      </div>  
        
        <!-- Sidebar zeigt nur Admin-Bereiche auf Admin-Routen an -->
        <!-- Tools werden in der Main-Navigation (Header) und im Dashboard angezeigt -->
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, inject, onMounted, defineAsyncComponent } from 'vue';
import { useRoute } from 'vue-router';
import { useAuthStore } from '../stores/auth';
import { useToolsStore } from '../stores/tools';
import { useWindowSize } from '@vueuse/core';

// Events definieren
const emit = defineEmits(['update:collapsed']);

// Stores
const authStore = useAuthStore();
const toolsStore = useToolsStore();
const route = useRoute();

// Fenstergröße überwachen (für responsive Anpassungen)
const { width } = useWindowSize();

// Development-Modus wurde entfernt

// Mobile-Erkennung (wird für bedingte Klassen verwendet)
const isMobile = computed(() => width.value < 768);

// Sidebar-Status mit Standardwert aus localStorage oder false
const isSidebarCollapsed = ref(false);

// Lade den gespeicherten Status beim Initialisieren
function loadSidebarState() {
  try {
    const savedState = localStorage.getItem('sidebarCollapsed');
    if (savedState !== null) {
      isSidebarCollapsed.value = JSON.parse(savedState);
    }
  } catch (e) {
    console.error('Fehler beim Laden des Sidebar-Status:', e);
  }
}

// Auth-bezogene computed properties
const isAuthenticated = computed(() => authStore.isAuthenticated);
const isAdmin = computed(() => authStore.isAdmin);

// Tool-Route-Detection
const isToolRoute = computed(() => {
  return route.path.startsWith('/tools/');
});

// Dynamisches Laden der Tool-Navigation
const currentToolNavigation = computed(() => {
  if (!isToolRoute.value) return null;
  
  // Tool-Name aus der Route extrahieren (z.B. /tools/tool2 -> tool2)
  const pathParts = route.path.split('/');
  if (pathParts.length < 3) return null;
  
  const toolName = pathParts[2];
  if (!toolName) return null;
  
  try {
    // Dynamisches Laden der Tool-Navigation-Komponente
    return defineAsyncComponent(() => 
      import(`../views/tools/${toolName}/components/Navigation.vue`)
        .catch(error => {
          console.warn(`Tool-Navigation für ${toolName} nicht gefunden:`, error);
          return null;
        })
    );
  } catch (error) {
    console.warn(`Fehler beim Laden der Tool-Navigation für ${toolName}:`, error);
    return null;
  }
});

// Prüfen, ob die aktuelle Route im Admin-Bereich ist
const isAdminRoute = computed(() => route.path.startsWith('/admin'));

// Admin-Zugriff wird über isAdmin computed property gehandhabt

// Header-Sichtbarkeit aus App.vue injizieren
const isHeaderVisible = inject('isHeaderVisible', ref(true));

// Reagieren auf Header-Sichtbarkeit
watch(isHeaderVisible, (newValue) => {
  // Sidebar-Position und Toggle-Button-Position anpassen
  if (newValue) {
    // Header ist sichtbar - Sidebar unter dem Header positionieren
    document.documentElement.style.setProperty('--sidebar-top', 'var(--header-total-height)');
  } else {
    // Header ist ausgeblendet - Sidebar unter der MainNav positionieren
    document.documentElement.style.setProperty('--sidebar-top', 'var(--nav-height)');
  }
});

// UI-Einstellungen laden
function loadUISettings() {
  try {
    // Einstellungen aus localStorage laden
    const uiSettings = JSON.parse(localStorage.getItem('uiSettings') || '{}');
    if (uiSettings.sidebarCollapsed !== undefined) {
      isSidebarCollapsed.value = uiSettings.sidebarCollapsed;
    } else {
      // Fallback auf ältere Speicherform
      const savedState = localStorage.getItem('sidebarCollapsed');
      if (savedState !== null) {
        isSidebarCollapsed.value = JSON.parse(savedState);
      }
    }
  } catch (error) {
    console.error('Fehler beim Laden der UI-Einstellungen:', error);
  }
}

// UI-Einstellungen speichern
function saveUISettings() {
  try {
    // In einheitlichem uiSettings-Objekt speichern
    const uiSettings = JSON.parse(localStorage.getItem('uiSettings') || '{}');
    uiSettings.sidebarCollapsed = isSidebarCollapsed.value;
    localStorage.setItem('uiSettings', JSON.stringify(uiSettings));
    
    // Für Abwärtskompatibilität auch noch in separatem Key speichern
    localStorage.setItem('sidebarCollapsed', JSON.stringify(isSidebarCollapsed.value));
  } catch (error) {
    console.error('Fehler beim Speichern der UI-Einstellungen:', error);
  }
}

// Sidebar ein-/ausklappen
function toggleSidebar() {
  isSidebarCollapsed.value = !isSidebarCollapsed.value;
  saveUISettings();
  // Status an übergeordnete Komponente emittieren
  emit('update:collapsed', isSidebarCollapsed.value);
}

// Mobile Sidebar schließen, wenn außerhalb geklickt wird
function closeSidebarOnMobile() {
  if (width.value < 768 && !isSidebarCollapsed.value) {
    isSidebarCollapsed.value = true;
    saveUISettings();
    // Status an übergeordnete Komponente emittieren
    emit('update:collapsed', isSidebarCollapsed.value);
  }
}

// Sidebar-Status bei Bildschirmgrößenänderung anpassen

// Automatisch die Sidebar einklappen, wenn die Bildschirmbreite unter 768px fällt
watch(width, (newWidth) => {
  if (newWidth < 768 && !isSidebarCollapsed.value) {
    isSidebarCollapsed.value = true;
    saveUISettings();
    // Status an übergeordnete Komponente emittieren
    emit('update:collapsed', isSidebarCollapsed.value);
  }
});

// Beim Initialisieren den Status an die übergeordnete Komponente kommunizieren
onMounted(() => {
  // Nach dem Laden der Einstellungen den Status emittieren
  setTimeout(() => {
    emit('update:collapsed', isSidebarCollapsed.value);
  }, 0);
  
  // Event-Listener für Routenänderungen hinzufügen
  // Bei mobilen Geräten die Sidebar automatisch einklappen, wenn sich die Route ändert
  if (typeof window !== 'undefined') {
    window.addEventListener('popstate', () => {
      if (width.value < 768 && !isSidebarCollapsed.value) {
        isSidebarCollapsed.value = true;
        saveUISettings();
        emit('update:collapsed', isSidebarCollapsed.value);
      }
    });
  }
});

// Komponente initialisieren
onMounted(async () => {
  await toolsStore.loadToolsAndPermissions();
  // Lade den gespeicherten Sidebar-Status
  loadSidebarState();
  loadUISettings();
  
  // Auf mobilen Geräten standardmäßig eingeklappt starten
  if (width.value < 768) {
    isSidebarCollapsed.value = true;
    saveUISettings();
    emit('update:collapsed', isSidebarCollapsed.value);
  }
  
  // Initiale Sidebar-Position basierend auf Header-Status setzen
  const headerState = isHeaderVisible.value ? 'var(--header-total-height)' : 'var(--nav-height)';
  document.documentElement.style.setProperty('--sidebar-top', headerState);
});
</script>

<style scoped>
/* Fallback-Nachricht, wenn keine Navigation verfügbar ist */
.no-nav-message {
  padding: var(--spacing-md);
  color: rgba(255, 255, 255, 0.7);
  font-style: italic;
  text-align: center;
}

.no-nav-message.collapsed {
  display: none;
}

/* Tool-Icon für eingeklappte Sidebar */
.tool-icon {
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1.2rem;
}

/* Dark-Mode Anpassungen für Sidebar */
:global(.mode-dark) .sidebar {
  background-color: var(--color-background-dark, #1d1d1b);
  border-right: 1px solid var(--color-border, #57747a);
  box-shadow: 2px 0 8px var(--shadow-color, rgba(0, 0, 0, 0.5));
}

:global(.mode-dark) .nav-link {
  color: var(--color-text, #ffffff);
}

:global(.mode-dark) .collapse-button {
  background-color: var(--color-background-darker);
}

:global(.mode-dark) .collapse-button:hover {
  background-color: var(--color-background-dark);
}

:global(.mode-dark) .nav-link:hover {
  background-color: var(--color-hover, rgba(29, 29, 27, 0.05));
}

:global(.mode-dark) .router-link-active {
  background-color: var(--color-background, #1d1d1b);
  border-left: 3px solid var(--color-accent);
  color: var(--color-text, #ffffff);
}

/* Dark Mode Anpassungen für Admin-Bereich */
:global(.mode-dark) .admin-section {
  margin-top: var(--spacing-lg);
  /* padding-top entfernt für konsistentes Dark Mode Layout */
}

:global(.mode-dark) .section-title {
  color: var(--color-accent-light);
}
/* Styles für Sidebar-Komponente */
.sidebar-container {
  position: relative;
  height: 100%; /* Volle Höhe des Parent-Containers (sidebar-wrapper) */
  display: flex; /* Flex-Container für die Sidebar-Inhalte */
  flex-direction: column; /* Vertikale Ausrichtung */
  /* Transition für smooth Breiten-Änderung */
  transition: width var(--sidebar-transition-duration) ease;
}

/* Debug-Panel Styles wurden entfernt */

/* Sidebar */
.sidebar {
  width: var(--sidebar-width); /* Responsive Breite aus CSS-Variablen */
  background-color: var(--color-primary-dark);
  color: var(--color-text-on-primary, white);
  flex: 1; /* Füllt den verfügbaren Raum im Flex-Container aus */
  height: 100%; /* Volle Höhe des Parent-Containers (page-wrapper) */
  min-height: 0; /* Verhindert Flexbox-Overflow-Probleme */
  overflow-y: auto;
  overflow-x: hidden; /* Verhindert horizontales Scrollen */
  /* Synchronisierte Transitions mit Main-Nav (0.3s ease) */
  transition: width var(--sidebar-transition-duration) ease, 
              background-color var(--sidebar-transition-duration) ease, 
              color var(--sidebar-transition-duration) ease;
  /* Position auf static geändert für Flexbox-Layout */
  position: static;
  /* top, bottom, left, z-index nicht mehr nötig bei static positioning */
  border-right: 1px solid var(--color-border, rgba(0, 0, 0, 0.1));
  /* Kein padding-top mehr nötig - page-wrapper ist bereits korrekt unter Header positioniert */
  scrollbar-width: thin; /* Schmalere Scrollbar für mehr Platz */
  scrollbar-color: var(--color-primary-light) transparent;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.1);
}

/* Header-Anpassungsregeln nicht mehr nötig - Sidebar ist jetzt im normalen Flexbox-Flow */

/* Eingeklappte Sidebar - Korrekte Selektoren für Transition */
.sidebar-container.sidebar-collapsed .sidebar {
  width: var(--sidebar-collapsed-width);
}

/* Section-Header - Überschriften mit Abstand */
.section-header {
  padding: var(--spacing-sm) var(--spacing-md);
  margin-bottom: var(--spacing-xs);
}

.section-header h3 {
  font-size: var(--font-size-base); /* Gleiche Größe wie Menüpunkte */
  font-weight: 600;
  color: var(--color-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0;
  transition: opacity var(--sidebar-transition-duration) ease;
}

/* Eingeklappte Sidebar - nur erster Buchstabe */
.sidebar-collapsed .section-header {
  padding: var(--spacing-sm) 0;
  text-align: center;
}

.sidebar-collapsed .section-header h3 {
  font-size: var(--font-size-base); /* Gleiche Größe wie ausgeklappt */
  font-weight: 600; /* Gleiche Schriftstärke wie ausgeklappt */
  overflow: hidden;
  white-space: nowrap;
  text-overflow: clip;
  width: 1.2em; /* Platz für einen Buchstaben mit etwas Puffer */
  text-align: center;
  margin: 0 auto;
  /* Zeige nur den ersten Buchstaben */
  text-indent: 0;
}

/* Sidebar-Navigation */
.sidebar-nav {
  padding: var(--spacing-md) 0;
}

/* Navigationslisten */
.nav-list, .sub-nav-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.nav-item {
  margin-bottom: var(--spacing-xs);
  position: relative;
}

/* Navigationslinks - Optimiert für Main-Nav-Höhe */
.nav-link {
  display: flex;
  align-items: center;
  height: var(--nav-height); /* Gleiche Höhe wie Main-Nav (48px) */
  padding: 0 var(--spacing-md);
  color: var(--color-text-on-primary, white);
  text-decoration: none;
  transition: background-color var(--sidebar-transition-duration) ease, color var(--sidebar-transition-duration) ease;
  border-left: 3px solid transparent;
  font-size: var(--font-size-base); /* Größere Schrift */
  gap: var(--spacing-sm); /* Konsistenter Abstand zwischen Icon und Text */
}

.sidebar-collapsed .nav-link {
  /* Gleiche Höhe wie ausgeklappt, nur zentrierte Icons */
  padding: 0; /* Kein Padding, damit Icons zentriert sind */
  justify-content: center;
  text-align: center;
}

.nav-link:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

/* Aktiver Link */
.router-link-active {
  background-color: rgba(255, 255, 255, 0.15);
  border-left: 3px solid var(--color-accent);
  font-weight: 500;
  color: var(--color-text-on-primary, white);
}

.sidebar-collapsed .router-link-active {
  border-left-color: transparent;
  border-bottom: 2px solid var(--color-accent);
}

/* Icons in der Navigation - Optimierte Größe */
.nav-icon {
  font-size: 1.2rem; /* Optimale Icon-Größe */
  width: 1.8rem; /* Icon-Container */
  height: 1.8rem; /* Icon-Container */
  text-align: center;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Icon-Abstand nur im ausgeklappten Zustand */
.nav-link:not(.sidebar-collapsed .nav-link) .nav-icon {
  margin-right: 0; /* Wird durch gap im nav-link geregelt */
}

/* Icons im eingeklappten Zustand - zentriert ohne margin */
.sidebar-collapsed .nav-icon {
  margin-right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}

/* Text in der Navigation */
.nav-text {
  font-weight: 500;
  transition: opacity var(--sidebar-transition-duration) ease;
  /* margin-left wird durch gap im nav-link geregelt */
  font-size: var(--font-size-md);
}

.sidebar-collapsed .nav-text {
  display: none;
}

/* Admin-Bereich */
.admin-section {
  margin-top: var(--spacing-lg);
  padding-top: var(--spacing-md);
}

/* Tool-Header Stile */
.tool-header h2 {
  padding: 0 var(--spacing-md);
  margin-bottom: var(--spacing-xs);
  font-weight: 600;
  font-size: var(--font-size-sm);
  color: var(--color-accent-light);
  text-transform: uppercase;
}

.collapsed-header h2 {
  text-align: center;
  font-size: var(--font-size-xs);
  padding: 0 var(--spacing-xs);
  color: var(--color-accent-light);
}

.sidebar-collapsed .tool-header {
  display: none;
}

/* Unternavigation */
.sub-nav-list {
  margin-left: var(--spacing-xs);
  color: var(--color-text, #ffffff);
}

/* Ein-/Ausklapp-Button für Desktop */
.collapse-button {
  position: fixed;
  top: calc(var(--sidebar-top, var(--header-total-height)) + 41px);
  left: var(--sidebar-width);
  width: 1px;
  height: 48px;
  border-radius: 0 4px 4px 0;
  background-color: var(--color-primary-dark);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 95; /* Unter der Sidebar, aber über dem Content */
  transition: left 0.3s ease, top 0.3s ease, background-color 0.3s ease;
  box-shadow: 2px 0 5px rgba(0, 0, 0, 0.1);
}

/* Button-Position anpassen, wenn Header ausgeblendet ist */
:global([data-header-state="hidden"]) .collapse-button {
  top: calc(var(--nav-height) + 1px);
  transition: top 0.3s ease;
}

button {
  padding: 0.6em 0.6em;
}

.sidebar-collapsed .collapse-button {
  left: var(--sidebar-collapsed-width);
}

.collapse-button:hover {
  background-color: var(--color-primary);
}

.collapse-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-on-primary, white);
  transition: transform 0.3s ease, color 0.3s ease;
}

:global(.mode-dark) .collapse-icon {
  color: var(--color-text, #ffffff);
}

/* Desktop/Mobile Klassen */
.desktop-only {
  display: none;
}

.mobile-only {
  display: flex;
}

@media (min-width: 768px) {
  .desktop-only {
    display: flex;
  }
  
  .mobile-only {
    display: none;
  }
}

/* Mobile Toggle Button */
.sidebar-toggle-button {
  position: fixed;
  flex-direction: column;
  bottom: 20px;
  right: 20px;
  width: 56px;
  height: 56px;
  color: white;
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background-color: var(--color-primary-dark);
  z-index: 101;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  padding: 16px;
  transition: all 0.3s ease;
}

.toggle-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Overlay für mobile Ansicht */
.sidebar-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 80;
}

/* Mobile Styles */
@media (max-width: 767px) {
  .sidebar-container {
    position: relative;
    z-index: 1000;
  }
  
  .sidebar {
    position: fixed;
    top: 15vh;
    left: 0;
    height: 90vh;
    width: min(85vw, var(--sidebar-width));
    background-color: var(--color-primary-dark, #006064);
    z-index: 1001;
    transform: translateX(-100%);
    transition: transform 0.3s ease-out, top 0.3s ease-out;
    box-shadow: 2px 0 10px rgba(0, 0, 0, 0.3);
    overflow-y: auto;
    border-radius: 0 8px 8px 0;
  }
  
  .sidebar-open {
    transform: translateX(0);
    top: 10vh;
  }
  
  .sidebar-hidden {
    transform: translateX(-100%);
    top: 0;
  }
  
  .sidebar-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 1000;
  }
  
  .sidebar-toggle-button {
    position: fixed;
    bottom: 20px;
    right: 20px;
    z-index: 1002;
    background-color: var(--color-primary-dark, #006064);
    border: none;
    border-radius: 50%;
    width: 56px;
    height: 56px;
    padding: 16px;
    color: white;
    cursor: pointer;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .sidebar-toggle-button:hover {
    background-color: var(--color-primary, #00796b);
    transform: scale(1.1);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.4);
  }
  
  .sidebar-toggle-button:active {
    transform: scale(0.95);
  }
  
  .desktop-only {
    display: none;
  }
}

/* Desktop Styles */
@media (min-width: 768px) {
  .mobile-only {
    display: none;
  }
}
</style>
